import React, { useState } from "react";
import { Col, Form, Input, notification, Row } from "antd";
import s from "./ConfirmPassword.module.scss";
import {
  CheckCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  KeyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { confirmPassword } from "../../redux/AuthSlice";
import { useTranslation } from "react-i18next";
import LoginHeader from "../../components/Header/LoginHeader";
import MobileLoginHeader from "../../components/MobileHeader/MobileLoginHeader";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";

const ConfirmPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [form] = useForm();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  const [searchParams] = useSearchParams();
  const token: string | null = searchParams.get("token");

  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const openNotification =
    (pauseOnHover: boolean, message: string, type: "success" | "error") =>
    () => {
      let icon;
      switch (type) {
        case "success":
          icon = <CheckCircleOutlined style={{ color: "green" }} />;
          break;

        case "error":
          icon = <WarningOutlined style={{ color: "red" }} />;
          break;
      }
      api.open({
        message,
        icon,
        showProgress: true,
        pauseOnHover,
      });
    };

  const confirmNewPassword = async (values: {
    new_password: string;
    confirm_new_password: string;
  }) => {
    try {
      if (token) {
        const res = await dispatch(
          confirmPassword({
            token: token,
            new_password: values.new_password,
            confirm_new_password: values.confirm_new_password,
          })
        );

        if (res.type === "auth/confirmNewPassword/fulfilled") {
          const message =
            res.payload && "message" in res.payload ? res.payload.message : "";

          openNotification(true, message, "success")();
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }

        if (res.type === "auth/confirmNewPassword/rejected") {
          const errorMessage =
            res.payload && "error" in res.payload
              ? res.payload.error
              : "Произошла ошибка";

          openNotification(true, errorMessage, "error")();
        }
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  return (
    <div>
      {width > 500 ? <LoginHeader /> : <MobileLoginHeader />}
      <div className={s.login}>
        {contextHolder}

        <h2 className={s.h2}>
          <KeyOutlined className={s.icon} />
          {t("Установите новый пароль")}
        </h2>
        <Form
          className={s.form}
          layout='vertical'
          form={form}
          onFinish={confirmNewPassword}
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name='new_password'
                label={t("Пароль")}
                className={s.formItem}
                rules={[
                  { required: true, message: t("Введите пароль") },
                  {
                    min: 8,
                    message: t("Пароль должен состоять минимум из 8 символов"),
                  },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                    message: t(
                      "Пароль должен иметь минимум одну заглавную, одну прописную буквы и цифру"
                    ),
                  },
                ]}
              >
                <Input.Password
                  className={s.password_input}
                  type={showPassword ? "text" : "password"}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeOutlined className={s.icon} />
                    ) : (
                      <EyeInvisibleOutlined />
                    )
                  }
                  suffix={
                    showPassword ? (
                      <EyeOutlined
                        onClick={togglePasswordVisibility}
                        style={{ color: "rgb(248, 116, 64)" }}
                      />
                    ) : (
                      <EyeInvisibleOutlined
                        onClick={togglePasswordVisibility}
                      />
                    )
                  }
                  placeholder={t("Введите пароль")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                name='confirm_new_password'
                label={<span>{t("Подтвердитe пароль")}</span>}
                dependencies={["new_password"]}
                className={s.formItem}
                rules={[
                  { required: true, message: t("Введите пароль еще раз") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("new_password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t("Пароли не совпадают!"))
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  className={s.password_input}
                  type={showPassword ? "text" : "password"}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeOutlined style={{ color: "rgb(248, 116, 64)" }} />
                    ) : (
                      <EyeInvisibleOutlined />
                    )
                  }
                  suffix={
                    showPassword ? (
                      <EyeOutlined
                        onClick={toggleConfirmPasswordVisibility}
                        className={s.icon}
                      />
                    ) : (
                      <EyeInvisibleOutlined
                        onClick={toggleConfirmPasswordVisibility}
                      />
                    )
                  }
                  placeholder={t("Введите пароль еще раз")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <button className={s.enter_btn}>{t("Отправить")} </button>
          </Row>
          <div className={s.no_account}>
            {t("Уже есть аккаунт?")} &nbsp;
            <Link to='/login' className={s.register_btn}>
              {t("Войти")}
            </Link>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ConfirmPassword;
